import { Directive, ElementRef, NgZone, OnDestroy, AfterViewInit, Input } from '@angular/core';

@Directive({
  selector: '[scrollActivity],[appScrollActivity]',
  standalone: true,
})
export class ScrollActivityDirective implements AfterViewInit, OnDestroy {
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private el: HTMLElement;
  // Scroll element may become an inner wrapper after refactor.
  private scrollEl: HTMLElement;
  private trackEl: HTMLElement | null = null;
  private thumbEl: HTMLElement | null = null;
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private resizeObserver: ResizeObserver | null = null;

  @Input() scrollActivityCustom: boolean = false; // enable custom overlay track/thumb
  private handler = () => {
    // Mark as actively scrolling.
    this.el.setAttribute('data-scrolling', 'true');
    if (this.timeoutId) clearTimeout(this.timeoutId);
    // Remove after short idle period.
    this.timeoutId = setTimeout(() => {
      this.el.removeAttribute('data-scrolling');
    }, 300);

    if (this.scrollActivityCustom) {
      this.updateThumb();
      this.showOverlayTrack();
    }
  };

  constructor(
    private ref: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {
    this.el = ref.nativeElement;
    this.scrollEl = this.el; // default before overlay refactor
  }

  ngAfterViewInit() {
    // Use passive listener for performance.
    this.zone.runOutsideAngular(() => {
      // For custom overlay we must create inner scroll element first, then attach listener to it.
      if (this.scrollActivityCustom) {
        this.initOverlay();
      }
      this.scrollEl.addEventListener('scroll', this.handler, { passive: true });
      if (this.scrollActivityCustom) {
        this.el.addEventListener('mouseenter', this.showOverlayTrack, { passive: true });
        this.el.addEventListener('mouseleave', this.scheduleHideOverlay, { passive: true });
      }
    });
  }

  ngOnDestroy() {
    this.scrollEl.removeEventListener('scroll', this.handler as EventListener);
    if (this.scrollActivityCustom) {
      this.el.removeEventListener('mouseenter', this.showOverlayTrack as EventListener);
      this.el.removeEventListener('mouseleave', this.scheduleHideOverlay as EventListener);
    }
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.hideTimeoutId) clearTimeout(this.hideTimeoutId);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  private initOverlay() {
    this.el.classList.add('overlay-scroll--custom');

    const originalChildren = Array.from(this.el.childNodes).filter(
      (n) => n.nodeType === Node.ELEMENT_NODE,
    ) as HTMLElement[];
    const inner = document.createElement('div');
    inner.className = 'overlay-scroll--inner';
    inner.style.height = '100%';
    inner.style.overflow = 'auto';
    inner.style.position = 'relative';
    this.el.style.overflow = 'hidden';
    originalChildren.forEach((c) => inner.appendChild(c));
    this.el.appendChild(inner);
    this.scrollEl = inner;

    this.trackEl = document.createElement('div');
    this.trackEl.className = 'overlay-scroll--track';
    this.thumbEl = document.createElement('div');
    this.thumbEl.className = 'overlay-scroll--thumb';
    this.trackEl.appendChild(this.thumbEl);
    this.el.appendChild(this.trackEl);
    this.updateThumb();
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateThumb());
      this.resizeObserver.observe(this.scrollEl);
      originalChildren.forEach((child) => {
        if (child instanceof HTMLElement) {
          this.resizeObserver?.observe(child);
        }
      });
    }
  }

  private updateThumb() {
    if (!this.thumbEl) return;
    const scrollHeight = this.scrollEl.scrollHeight;
    const clientHeight = this.scrollEl.clientHeight;
    if (scrollHeight <= clientHeight) {
      this.trackEl?.style.setProperty('display', 'none');
      return;
    }
    this.trackEl?.style.setProperty('display', '');
    const ratio = clientHeight / scrollHeight;
    const thumbHeight = Math.max(24, clientHeight * ratio);
    const scrollTop = this.scrollEl.scrollTop;
    const maxScroll = scrollHeight - clientHeight;
    const trackHeight = this.trackEl ? this.trackEl.clientHeight : clientHeight;
    const available = trackHeight - thumbHeight;
    const top = available <= 0 ? 0 : (scrollTop / maxScroll) * available;
    this.thumbEl.style.height = `${thumbHeight}px`;
    this.thumbEl.style.transform = `translate3d(0, ${top}px, 0)`;
    this.thumbEl.setAttribute('data-thumb-top', String(Math.round(top)));
  }

  private showOverlayTrack = () => {
    if (!this.trackEl) return;
    this.trackEl.style.opacity = '1';
    if (this.hideTimeoutId) clearTimeout(this.hideTimeoutId);
  };

  private scheduleHideOverlay = () => {
    if (!this.trackEl) return;
    if (this.hideTimeoutId) clearTimeout(this.hideTimeoutId);
    this.hideTimeoutId = setTimeout(() => {
      this.trackEl && (this.trackEl.style.opacity = '0');
    }, 500);
  };
}
